import ParametersRepository from "@/core/parameters/repositories/ParametersRepository";
import {
  AccountPurposesResponse,
  AgenciesBackendResponse,
  AgenciesResponse,
  Agency,
  CivilitiesResponse,
  CountriesResponse,
  DistributionChannelsResponse,
  FamilyStatusResponse,
  IncomeSourcesResponse,
  NationalitiesResponse,
  ProductOptionsResponse,
  ProfessionCategoriesResponse,
  ProfessionsResponse,
  RiskLookupItem,
  SectorActivitiesResponse,
} from "@/core/parameters/entities/ParameterTypes";
import { RiskLevel } from "@/core/administration/entities/RiskEntityCode";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import { ApiResponseHandler } from "../../http/services/handleApiResponse/handleApiResponse";
import {
  getAccountPurposesUrl,
  getAgenciesUrl,
  getCivilitiesUrl,
  getCountriesUrl,
  getDistributionChannelsUrl,
  getFamilyStatusUrl,
  getIncomeSourcesUrl,
  getNationalitiesUrl,
  getProductOptionsUrl,
  getProfessionCategoriesUrl,
  getProfessionsUrl,
  getSectorActivitiesUrl,
} from "../../api/parametersBackendEndpoints/parametersBackendEndpoints";

export default class ParametersRepositoryBackend implements ParametersRepository {
  constructor(private readonly httpClient: HttpClient) {}

  private async fetchSimpleList<R extends { code: string; message: string; data: unknown[] }>(
    url: string,
  ): Promise<R> {
    const response = await this.httpClient.get<ApiResponse<unknown>>({ url, withAuth: true });
    const result = ApiResponseHandler.handleResponse(response);
    return {
      code: result.code || "200",
      message: result.message || "Success",
      data: Array.isArray(result.data) ? result.data : [],
    } as unknown as R;
  }

  private async fetchRiskLookupList(
    url: string,
  ): Promise<{ code: string; message: string; data: RiskLookupItem[] }> {
    const response = await this.httpClient.get<ApiResponse<unknown>>({ url, withAuth: true });
    const result = ApiResponseHandler.handleResponse(response);
    return {
      code: result.code || "200",
      message: result.message || "Success",
      data: Array.isArray(result.data)
        ? result.data.map((item) => {
            const i = item as { id?: number; code?: string; label?: string; riskLevel?: string };
            return {
              id: Number(i.id ?? 0),
              code: i.code ?? "",
              label: i.label ?? "",
              riskLevel: (i.riskLevel ?? "RF") as RiskLevel,
            };
          })
        : [],
    };
  }

  async getCivilities(): Promise<CivilitiesResponse> {
    return this.fetchSimpleList<CivilitiesResponse>(getCivilitiesUrl());
  }

  async getCountries(): Promise<CountriesResponse> {
    return this.fetchSimpleList<CountriesResponse>(getCountriesUrl());
  }

  async getFamilyStatus(): Promise<FamilyStatusResponse> {
    return this.fetchSimpleList<FamilyStatusResponse>(getFamilyStatusUrl());
  }

  async getNationalities(): Promise<NationalitiesResponse> {
    return this.fetchSimpleList<NationalitiesResponse>(getNationalitiesUrl());
  }

  async getProductOptions(): Promise<ProductOptionsResponse> {
    return this.fetchSimpleList<ProductOptionsResponse>(getProductOptionsUrl());
  }

  async getProfessionCategories(): Promise<ProfessionCategoriesResponse> {
    return this.fetchSimpleList<ProfessionCategoriesResponse>(getProfessionCategoriesUrl());
  }

  async getSectorActivities(): Promise<SectorActivitiesResponse> {
    return this.fetchSimpleList<SectorActivitiesResponse>(getSectorActivitiesUrl());
  }

  async getProfessions(): Promise<ProfessionsResponse> {
    const response = await this.httpClient.get<ApiResponse<unknown>>({
      url: getProfessionsUrl(),
      withAuth: true,
    });
    const result = ApiResponseHandler.handleResponse(response);
    return {
      code: result.code || "200",
      message: result.message || "Success",
      data: Array.isArray(result.data)
        ? result.data.map((profession) => {
            const p = profession as {
              code?: string;
              libelle?: string;
              dateCreation?: string;
              dateModification?: string;
            };
            return {
              code: p.code ?? "",
              name: p.libelle ?? "",
              dateCreation: p.dateCreation ?? "",
              dateModification: p.dateModification ?? "",
            };
          })
        : [],
    };
  }

  async getAgencies(): Promise<AgenciesResponse> {
    const response = await this.httpClient.get<AgenciesBackendResponse>({
      url: getAgenciesUrl(),
      withAuth: true,
    });
    const result = ApiResponseHandler.handleResponse(response);

    const mappedAgencies = result.data?.items?.map((agency) => ({
      code: agency.code,
      name: agency.nom,
      fullName: agency.nomComplet,
      type: agency.type,
      address: agency.adresse,
      coordinates: agency.coordonnees,
      openingHours: agency.horaires,
      services: agency.services,
      contact: agency.contact,
      createdAt: agency.dateCreation,
      updatedAt: agency.dateModification,
      isActive: agency.actif,
    })) as Agency[];

    return {
      code: result.code || "200",
      message: result.message || "Success",
      data: {
        items: Array.isArray(result.data?.items) ? mappedAgencies : [],
        totalElements: result.data?.totalElements || 0,
      },
    };
  }

  async getAccountPurposes(): Promise<AccountPurposesResponse> {
    return this.fetchRiskLookupList(getAccountPurposesUrl());
  }

  async getIncomeSources(): Promise<IncomeSourcesResponse> {
    return this.fetchRiskLookupList(getIncomeSourcesUrl());
  }

  async getDistributionChannels(): Promise<DistributionChannelsResponse> {
    return this.fetchRiskLookupList(getDistributionChannelsUrl());
  }
}
